import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { Link, useParams } from 'react-router-dom';
import AppSidebar from './AppSidebar';
import { Navbar } from './common/Catalyst/navbar';
import { SidebarLayout } from './common/Catalyst/sidebar-layout';
import { Button } from './common/Catalyst/button';
import LoadingSpinner from './LoadingSpinner';
import { amenityService, reservationService } from './clients/ApiClients';
import {
  AmenityReturn,
  ReservationEventReturn,
  ReservationReturn,
} from './clients/rust-api-client';
import {
  canAttachToReservationComment,
  canManageReservations,
  canModerateViolationComments,
  canPostInternalComment,
  canPostReservationComment,
  canViewReservationComments,
} from './constants/UserTypes';
import { getUserIdFromToken, getUserTypeFromToken } from './utils/TokenUtils';
import { savePdfResponse } from './utils/billingHelpers';
import CommentThread from './violation/comments/CommentThread';
import { ReservationStatusIcon } from './reservation/ReservationStatusIcon';
import { ReservationPropertiesPanel } from './reservation/ReservationPropertiesPanel';
import { ReservationActivity } from './reservation/ReservationActivity';
import {
  Decision,
  ReservationDecisionDialogs,
} from './reservation/ReservationDecisionDialogs';
import { RescheduleDialog } from './reservation/RescheduleDialog';
import {
  AccessSection,
  DepositSection,
  DocumentsSection,
  InspectionsSection,
  ReturnMoneyAction,
  ReturnedNote,
} from './reservation/ReservationOperations';
import { invalidatePendingReservationCount } from './reservation/usePendingReservationCount';
import {
  formatLocalRange,
  reservationErrorMessage,
  shortRef,
  statusLabel,
} from './reservation/reservationMeta';
import { inputClass } from './reservation/formStyles';
import { ErrorAlert } from './common/Custom/ErrorAlert';
import { useSyncedAmenity, useSyncedReservation } from './sync/reservationHooks';
import { OwnReservation, shownReservation } from './reservation/shownReservation';

/**
 * One reservation (#642): what was booked, the decision buttons, the two
 * notes, and the full event trail. Reached from the queue, the calendar,
 * a home's page, or a notification.
 *
 * A board member sees the booking and its history but no decision
 * buttons and no internal note — the API leaves `internal_notes` out of
 * what it sends them, and this page does not offer to write one.
 *
 * Phase 2 adds: reschedule in place, mark a no-show, a calendar file,
 * the series a booking belongs to, and the two comment threads (internal,
 * and shared with the household) under "Conversation".
 *
 * Phase 3 adds the booking's operations: access code and check-in, the
 * deposit and its settlement, required documents and their review, and
 * inspections with photos (`reservation/ReservationOperations`).
 */

const ReservationDetailPage: React.FC = () => {
  const { reservationId, hoaId } = useParams<{ reservationId: string; hoaId?: string }>();
  // From the sync engine's copy when it holds this booking (#561): the
  // page renders at once, and its sections start loading at once too.
  const synced = useSyncedReservation(reservationId);
  const syncedRef = useRef(synced);
  syncedRef.current = synced;
  const [own, setOwn] = useState<OwnReservation | null>(null);
  const setReservation = useCallback(
    (row: ReservationReturn) => setOwn({ row, copy: syncedRef.current }),
    []
  );
  const reservation = useMemo(() => shownReservation(synced, own), [synced, own]);
  const [events, setEvents] = useState<ReservationEventReturn[]>([]);
  const [fetching, setLoading] = useState(true);
  const loading = !synced && fetching;
  const [error, setError] = useState<string | null>(null);
  const [decision, setDecision] = useState<Decision | null>(null);
  const [rescheduling, setRescheduling] = useState(false);
  const [fetchedAmenity, setAmenity] = useState<AmenityReturn | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const userType = getUserTypeFromToken();
  const canManage = canManageReservations(userType);

  const loadEvents = useCallback(async (id: string) => {
    try {
      setEvents((await reservationService.listReservationEvents({ reservationId: id })).data.events);
    } catch {
      // The booking itself still shows; the trail is secondary.
    }
  }, []);

  useEffect(() => {
    if (!reservationId) return;
    let live = true;
    setLoading(true);
    setError(null);
    // Always read: the copy lacks the access code, the notes and the
    // per-read fields. With a copy this fills them in behind the page.
    reservationService
      .getReservation({ reservationId })
      .then((r) => live && setReservation(r.data))
      .catch((err) => live && setError(reservationErrorMessage(err, "Couldn't load the reservation.")))
      .finally(() => live && setLoading(false));
    loadEvents(reservationId);
    return () => {
      live = false;
    };
  }, [reservationId, loadEvents, setReservation]);

  // The booking changed elsewhere — approved by a colleague, cancelled by
  // the household: what REST works out per read (the check-in window, the
  // cancel outcome) may have changed with it, and so may the history.
  useEffect(() => {
    if (!reservationId || !synced || !own || own.copy === synced) return;
    let live = true;
    reservationService
      .getReservation({ reservationId })
      .then((r) => live && setReservation(r.data))
      // The copy is still shown; only the per-read fields are older.
      .catch(() => undefined);
    loadEvents(reservationId);
    return () => {
      live = false;
    };
    // `own` is read for its copy marker only; refetching on every `own`
    // change would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservationId, synced]);

  // The amenity's mode and limits, for rescheduling — from the copy when
  // it holds it.
  const amenityId = reservation?.amenity_id;
  const syncedAmenity = useSyncedAmenity(amenityId, reservation?.hoa_id);
  const haveSyncedAmenity = !!syncedAmenity;
  const amenity = syncedAmenity ?? fetchedAmenity;
  useEffect(() => {
    if (!amenityId || haveSyncedAmenity) return;
    let live = true;
    amenityService
      .getAmenity({ amenityId })
      .then((a) => live && setAmenity(a.data))
      .catch(() => {
        // Reschedule falls back to the booking's own length.
      });
    return () => {
      live = false;
    };
  }, [amenityId, haveSyncedAmenity]);

  const downloadCalendar = async (id: string) => {
    setDownloadError(null);
    try {
      const res = await reservationService.getReservationCalendar(
        { reservationId: id },
        { responseType: 'blob' }
      );
      savePdfResponse(res, `${shortRef(id)}.ics`);
    } catch (err) {
      setDownloadError(reservationErrorMessage(err, "Couldn't download the calendar file."));
    }
  };

  const onDecided = (updated: ReservationReturn) => {
    setReservation(updated);
    loadEvents(updated.id);
    invalidatePendingReservationCount();
  };

  /** A phase-3 operation finished: take its returned booking, or fetch it again. */
  const onOperated = async (updated?: ReservationReturn) => {
    if (updated) {
      onDecided(updated);
      return;
    }
    if (!reservationId) return;
    try {
      setReservation((await reservationService.getReservation({ reservationId })).data);
    } catch {
      // The section that ran already showed its own outcome.
    }
    loadEvents(reservationId);
  };

  if (loading) return <LoadingSpinner />;

  const listPath = hoaId || reservation ? `/hoa/${hoaId ?? reservation!.hoa_id}/reservations` : '/reservations';
  const r = reservation;
  const isOver = r ? !['pending', 'approved'].includes(r.status) : true;
  const started = r ? r.starts_at * 1000 <= Date.now() : false;
  const canNoShow = !!r && canManage && started && (r.status === 'approved' || r.status === 'completed');

  return (
    <SidebarLayout
      sidebar={<AppSidebar currentPage="reservations" />}
      navbar={<Navbar>{/* Your navbar content */}</Navbar>}
    >
      <div className="-mx-2 mb-6 flex items-center gap-2 border-b border-line/60 px-2 pb-3 text-[13px]">
        <Link to={listPath} className="shrink-0 whitespace-nowrap text-fg-muted hover:text-fg">
          Reservations
        </Link>
        <span className="text-fg-faint" aria-hidden>
          /
        </span>
        {r && (
          <>
            <ReservationStatusIcon status={r.status} />
            <span className="hidden font-mono text-[12px] text-fg-muted sm:inline">{shortRef(r.id)}</span>
            <span className="min-w-0 truncate font-medium text-fg">{r.amenity_name}</span>
          </>
        )}
      </div>

      <ErrorAlert>{error}</ErrorAlert>

      {r && (
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1fr)_260px]">
          <div className="min-w-0 space-y-8">
            <div>
              <p className="text-[13px] font-medium text-fg-muted">{statusLabel(r.status)}</p>
              <h1 className="mt-1 text-2xl/8 font-semibold tracking-tight text-fg">
                {r.amenity_name} · {r.home_address}
              </h1>
              <p className="mt-2 text-[15px]/6 text-fg-secondary">
                {formatLocalRange(r.start_local, r.end_local, new Date().getFullYear())}
                <span className="text-fg-muted"> · Times in {r.timezone}</span>
              </p>
              {r.purpose && (
                <p className="mt-3 whitespace-pre-line text-[15px]/6 text-fg-secondary">{r.purpose}</p>
              )}
            </div>

            {((canManage && (!isOver || canNoShow)) || r.status === 'approved') && (
              <div className="flex flex-wrap gap-2">
                {canManage && r.status === 'pending' && (
                  <>
                    <Button color="sky" onClick={() => setDecision('approve')}>
                      Approve
                    </Button>
                    <Button outline onClick={() => setDecision('deny')}>
                      Deny
                    </Button>
                  </>
                )}
                {canManage && !isOver && !started && (
                  <Button outline onClick={() => setRescheduling(true)}>
                    Reschedule
                  </Button>
                )}
                {canNoShow && (
                  <Button outline onClick={() => setDecision('no_show')}>
                    Mark no-show
                  </Button>
                )}
                {canManage && !isOver && (
                  <Button plain onClick={() => setDecision('cancel')}>
                    Cancel reservation
                  </Button>
                )}
                {r.status === 'approved' && (
                  <Button plain onClick={() => downloadCalendar(r.id)}>
                    Add to calendar (.ics)
                  </Button>
                )}
              </div>
            )}
            <ErrorAlert>{downloadError}</ErrorAlert>
            {r.series_id && (
              <p className="text-[13px] text-fg-muted">
                One of a recurring series ·{' '}
                <Link
                  to={`/hoa/${r.hoa_id}/reservations/series/${r.series_id}`}
                  className="text-accent-fg hover:underline"
                >
                  See the series
                </Link>
              </p>
            )}

            {r.status === 'pending' && !canManage && (
              <p className="text-[13px] text-fg-muted">Waiting for a manager to approve or deny it.</p>
            )}
            {r.fee_status === 'late_fee' && (
              <p className="rounded-md bg-fg/5 px-3 py-2 text-[13px] text-fg-secondary">
                Cancelled after the free-cancellation deadline: the fee was reversed and a late-cancellation
                fee charged instead.
              </p>
            )}
            <ReturnedNote what="fee" status={r.fee_status} amount={r.fee_amount} />
            <ReturnMoneyAction reservation={r} canManage={canManage} onChanged={onOperated} />

            <AccessSection reservation={r} amenity={amenity} canManage={canManage} onChanged={onOperated} />
            <DepositSection reservation={r} canManage={canManage} onChanged={onOperated} />
            <DocumentsSection reservation={r} canManage={canManage} onChanged={onOperated} />
            <InspectionsSection reservation={r} amenity={amenity} canManage={canManage} onChanged={onOperated} />

            <NoteEditor
              title="Note to the household"
              help="Shown to the homeowner with the booking — the reason for a denial or cancellation lands here too."
              value={r.decision_note ?? ''}
              editable={canManage}
              onSave={async (decision_note) =>
                onDecided(
                  (
                    await reservationService.patchReservation({
                      reservationId: r.id,
                      reservationPatch: { decision_note },
                    })
                  ).data
                )
              }
            />
            {canManage && (
              <NoteEditor
                title="Internal note"
                help="Managers only — never shown to the household or the board."
                value={r.internal_notes ?? ''}
                editable
                onSave={async (internal_notes) =>
                  onDecided(
                    (
                      await reservationService.patchReservation({
                        reservationId: r.id,
                        reservationPatch: { internal_notes },
                      })
                    ).data
                  )
                }
              />
            )}

            <ReservationActivity events={events} bookedByName={r.booked_by_name} title="History" />

            {canViewReservationComments(userType) && (
              <CommentThread
                subject={{ kind: 'reservation', id: r.id }}
                currentUserId={getUserIdFromToken()}
                canComment={canPostReservationComment(userType)}
                canPostInternal={canPostInternalComment(userType)}
                canModerate={canModerateViolationComments(userType)}
                canAttach={canAttachToReservationComment(userType)}
                title="Conversation"
              />
            )}
          </div>

          <aside aria-label="Reservation properties" className="lg:sticky lg:top-6 lg:self-start">
            <ReservationPropertiesPanel reservation={r} />
          </aside>
        </div>
      )}

      {r && canManage && (
        <>
          <ReservationDecisionDialogs
            reservation={r}
            decision={decision}
            onClose={() => setDecision(null)}
            onDecided={onDecided}
          />
          <RescheduleDialog
            open={rescheduling}
            reservation={r}
            amenity={amenity}
            onClose={() => setRescheduling(false)}
            onDecided={onDecided}
          />
        </>
      )}
    </SidebarLayout>
  );
};

/** A note with an Edit / Save cycle; read-only text where editing is not allowed. */
const NoteEditor: React.FC<{
  title: string;
  help: string;
  value: string;
  editable: boolean;
  onSave: (value: string) => Promise<void>;
}> = ({ title, help, value, editable, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setDraft(value), [value]);

  if (!editable && !value) return null;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(draft.trim());
      setEditing(false);
    } catch (err) {
      setError(reservationErrorMessage(err, "Couldn't save the note."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section>
      <div className="mb-2 flex items-center gap-2">
        <h2 className="text-[13px] font-medium text-fg-muted">{title}</h2>
        {editable && !editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-[12px] text-accent-fg hover:underline"
          >
            {value ? 'Edit' : 'Add'}
          </button>
        )}
      </div>
      {editing ? (
        <div className="space-y-2">
          <textarea
            rows={3}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className={clsx(inputClass, 'w-full resize-y')}
            aria-label={title}
          />
          <p className="text-[12px] text-fg-muted">{help}</p>
          <ErrorAlert>{error}</ErrorAlert>
          <div className="flex gap-2">
            <Button color="sky" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
            <Button
              plain
              onClick={() => {
                setDraft(value);
                setEditing(false);
              }}
              disabled={saving}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : value ? (
        <p className="whitespace-pre-line rounded-lg border border-line px-4 py-3 text-[13px]/5 text-fg-secondary">
          {value}
        </p>
      ) : (
        <p className="text-[13px] text-fg-muted">None.</p>
      )}
    </section>
  );
};

export default ReservationDetailPage;
